/**
 * Live W8 board-telemetry → Soft geometry meters.
 *
 * Server decides freshness (STALE_AFTER_S). When fresh=false the payload is
 * emptied — CMC must render unknown, never Soft random walks or frozen
 * brochure temperatures. Pin geometry stays Soft; values are Live or blank.
 */

import { seatIdFromSoftSeat } from "./board-seat-adapter.js";

/** Soft silicon id → telemetry.silicon key from the Waveshare P4 profile. */
export const SILICON_TELE_KEY = {
  p4mod: "soc",
  flash: "psram",
  c6: "c6",
  phy: "phy",
};

/**
 * Overlay GET /board-telemetry pin levels onto Soft header pins.
 * @param {object[]} softPins
 * @param {object|null} tele board-telemetry response
 */
export function applyPinTelemetry(softPins, tele) {
  const fresh = !!(tele && tele.fresh);
  const age = tele && tele.age_s != null ? Number(tele.age_s) : null;
  const pinLive = (tele && tele.payload && tele.payload.pins) || {};
  return (softPins || []).map((p) => {
    const seatId = String(p.seat_id || seatIdFromSoftSeat(p.seat) || "").toUpperCase();
    if (!fresh) {
      return {
        ...p,
        live: null,
        probe: age == null ? "no telemetry yet" : ("stale · " + age + " s"),
        teleFresh: false,
        teleAge: age,
      };
    }
    const row = pinLive[seatId] || pinLive[seatId.replace(/^HDR-0+/, "HDR-")];
    if (!row || typeof row !== "object") {
      return {
        ...p,
        live: p.role ? { kind: "digital", value: null, age: age || 0, unit: "" } : null,
        probe: p.probe && !/Soft|brochure/i.test(String(p.probe)) ? p.probe : "no level in frame",
        teleFresh: true,
        teleAge: age,
      };
    }
    const level = row.level == null ? null : Number(row.level);
    const probe = String(row.probe || p.probe || "unknown");
    return {
      ...p,
      live: {
        kind: "digital",
        value: level,
        age: age || 0,
        unit: "",
        force: row.force || null,
        active: !!row.active,
        bound: !!row.bound,
      },
      probe,
      teleFresh: true,
      teleAge: age,
    };
  });
}

/**
 * Drive Soft SILICON fixtures from Live silicon block. Unmapped chips lose
 * Soft invented meters when boardLive — unknown beats a brochure °C.
 */
export function applySiliconTelemetry(siliconList, tele) {
  const fresh = !!(tele && tele.fresh);
  const age = tele && tele.age_s != null ? Number(tele.age_s) : null;
  const byId = {};
  for (const s of (tele && tele.silicon) || []) {
    if (s && s.id) byId[String(s.id)] = s.live || {};
  }
  /* Also accept payload.silicon when top-level silicon[].live is empty. */
  const payloadSil = (tele && tele.payload && tele.payload.silicon) || {};
  for (const [k, v] of Object.entries(payloadSil)) {
    if (!byId[k] || !Object.keys(byId[k]).length) byId[k] = v || {};
  }

  return (siliconList || []).map((c) => {
    const key = SILICON_TELE_KEY[c.id];
    if (!key) {
      /* No Live channel — strip Soft walk when telemetry surface is armed. */
      if (tele) {
        return { ...c, live: null, temp: null, teleFresh: fresh, teleAge: age };
      }
      return c;
    }
    if (!fresh) {
      return {
        ...c,
        live: null,
        temp: null,
        teleFresh: false,
        teleAge: age,
        sub: age == null
          ? (c.sub || "") + " · no frame"
          : (c.sub || "") + " · stale " + age + " s",
      };
    }
    const live = byId[key] || {};
    if (c.id === "p4mod") {
      const temp = live.temp_c != null ? Number(live.temp_c) : null;
      const heap = live.free_heap_b != null ? Number(live.free_heap_b) : null;
      return {
        ...c,
        temp,
        live: temp != null
          ? { kind: "temp", value: temp, unit: "°C", age: age || 0 }
          : null,
        sub: heap != null
          ? ("P4 · free heap " + Math.round(heap / 1024) + " KB")
          : c.sub,
        teleFresh: true,
        teleAge: age,
      };
    }
    if (c.id === "flash") {
      const free = live.free_b != null ? Number(live.free_b) : null;
      const total = live.total_b != null ? Number(live.total_b) : null;
      return {
        ...c,
        live: free != null
          ? { kind: "bus", value: Math.round(free / (1024 * 1024)), unit: "MB free", age: age || 0 }
          : null,
        temp: null,
        sub: total != null
          ? ("PSRAM · " + Math.round(free / (1024 * 1024)) + "/" + Math.round(total / (1024 * 1024)) + " MB free")
          : c.sub,
        teleFresh: true,
        teleAge: age,
      };
    }
    return { ...c, live: null, temp: null, teleFresh: true, teleAge: age };
  });
}

/**
 * Extract PoE PD live fields from board-telemetry (profile silicon id `poe_pd`).
 * Never invents watts/class — blank when fresh frame lacks measured keys.
 *
 * @param {object|null} tele
 * @returns {{ fresh: boolean, age_s: number|null, fields: null|{ watts: number|null, classN: string|number|null, vin: number|null, uptime: number|null } }}
 */
export function poeLiveFromTelemetry(tele) {
  if (!tele) return { fresh: false, age_s: null, fields: null };
  const age = tele.age_s != null ? Number(tele.age_s) : null;
  if (!tele.fresh) return { fresh: false, age_s: age, fields: null };
  const row = (tele.silicon || []).find((s) => s && s.id === "poe_pd");
  const live = (row && row.live && typeof row.live === "object")
    ? row.live
    : ((tele.payload && tele.payload.silicon && tele.payload.silicon.poe_pd) || {});
  const wattsRaw = live.watts_w != null ? live.watts_w
    : (live.power_w != null ? live.power_w : (live.watts != null ? live.watts : null));
  const watts = wattsRaw == null ? null : Number(wattsRaw);
  const classN = live.class != null ? live.class
    : (live.ieee_class != null ? live.ieee_class : (live.pd_class != null ? live.pd_class : null));
  const vinRaw = live.vin_v != null ? live.vin_v : (live.v_in != null ? live.v_in : null);
  const vin = vinRaw == null ? null : Number(vinRaw);
  const uptimeRaw = live.uptime_s != null ? live.uptime_s : tele.uptime_s;
  const uptime = uptimeRaw == null ? null : Number(uptimeRaw);
  const hasAny = (watts != null && Number.isFinite(watts))
    || (classN != null && String(classN).trim() !== "")
    || (vin != null && Number.isFinite(vin));
  return {
    fresh: true,
    age_s: age,
    fields: hasAny
      ? {
          watts: watts != null && Number.isFinite(watts) ? watts : null,
          classN: classN != null && String(classN).trim() !== "" ? classN : null,
          vin: vin != null && Number.isFinite(vin) ? vin : null,
          uptime: uptime != null && Number.isFinite(uptime) ? uptime : null,
        }
      : null,
  };
}

/**
 * Map GET /storage/card → TF UI state + honest capacity line.
 * @returns {{ state: string, title: string, detail: string, canAct: boolean, actLabel: string|null }}
 */
export function storageCardUi(card) {
  if (!card) {
    return {
      state: "unknown",
      title: "Unknown",
      detail: "No storage/card frame yet",
      canAct: false,
      actLabel: null,
    };
  }
  const fresh = !!card.fresh;
  const present = !!card.present;
  const mounted = !!card.mounted;
  const cap = card.capacity_mb != null ? Number(card.capacity_mb) : null;
  const free = card.free_mb != null ? Number(card.free_mb) : null;
  const health = String(card.health || "unknown");
  const sizeLine = [
    Number.isFinite(cap) ? (cap + " MB capacity") : null,
    Number.isFinite(free) ? (free + " MB free") : null,
    health !== "unknown" ? ("health " + health) : null,
    card.fs ? String(card.fs) : null,
    fresh ? null : (card.age_s != null ? ("stale " + card.age_s + " s") : "stale"),
  ].filter(Boolean).join(" · ") || "cold store · waiting for device report";

  if (!present) {
    return {
      state: "out",
      title: "Not present",
      detail: sizeLine,
      canAct: false,
      actLabel: null,
    };
  }
  if (mounted) {
    return {
      state: "mounted",
      title: fresh ? "Mounted" : "Mounted · stale report",
      detail: sizeLine,
      canAct: true,
      actLabel: "Unmount and eject",
    };
  }
  return {
    state: "out",
    title: fresh ? "Present · unmounted" : "Present · stale report",
    detail: sizeLine,
    canAct: true,
    actLabel: "Mount card",
  };
}

/** Soft fixture meters: freeze brochure numbers — no Math.random theatre. */
export function freezeSoftMeters(pin) {
  if (!pin || !pin.live) return pin;
  return { ...pin, live: { ...pin.live, age: pin.live.age || 0 }, softFixture: true };
}
